<template>
  <div>
    <PageListTemplate :title="title" :subtitle="subtitle" :add="add">
      <template #default>
        <DataTableTemplate
          class="mt-4"
          no-data-text="Nenhum pedido cadastrado"
          :items="items"
          :height="tableHeight"
          :loading="isLoading"
          :headers="headers"
        >
          <template v-slot:item.id="{ item }">
            <div class="primary--text text-body-2 font-weight-medium">
              {{ item.id }}
            </div>
          </template>

          <template v-slot:item.client_id="{ item }">
            <div
              class="secondary--text text--darken-2 text-body-2 font-weight-medium"
            >
              {{ item.client?.name }}
            </div>
          </template>

          <template v-slot:item.total_area_min="{ item }">
            <div class="position-relative">
              <v-tooltip
                v-if="
                  !item.metric_system &&
                  (item.total_area_min || item.total_area_max)
                "
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    class="alert-inconsistency"
                    color="#fe9a00"
                    size="16"
                  >
                    mdi-alert-circle-outline
                  </v-icon>
                </template>

                <span>Unidade de área não cadastrada</span>
              </v-tooltip>

              <div class="secondary--text">
                {{
                  formatAreaMinMax(
                    {
                      min: item.total_area_min,
                      max: item.total_area_max,
                    },
                    item.metric_system,
                  )
                }}
              </div>
            </div>
          </template>

          <template v-slot:item.open_area_min="{ item }">
            <div class="position-relative">
              <v-tooltip
                v-if="
                  !item.metric_system &&
                  (item.open_area_min || item.open_area_max)
                "
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    class="alert-inconsistency"
                    color="#fe9a00"
                    size="16"
                  >
                    mdi-alert-circle-outline
                  </v-icon>
                </template>

                <span>Unidade de área não cadastrada</span>
              </v-tooltip>

              <div class="secondary--text">
                {{
                  formatAreaMinMax(
                    {
                      min: item.open_area_min,
                      max: item.open_area_max,
                    },
                    item.metric_system,
                  )
                }}
              </div>
            </div>
          </template>

          <template v-slot:item.purpose="{ item }">
            <v-chip
              v-if="formatPurpose(item.purpose)"
              class="chip-control"
              :style="colorPurposeById(item.purpose)"
            >
              {{ formatPurpose(item.purpose) }}
            </v-chip>
          </template>

          <template v-slot:item.business="{ item }">
            <div class="secondary--text">
              {{ formatBusiness(item.business) }}
            </div>
          </template>

          <template v-slot:item.status="{ item }">
            <div class="secondary--text">
              {{ formatStatus(item.status) }}
            </div>
          </template>

          <template v-slot:item.price_min="{ item }">
            <div class="secondary--text">
              {{
                formatPriceMinMax({ min: item.price_min, max: item.price_max })
              }}
            </div>
          </template>

          <template v-slot:item.created_at="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="d-inline secondary--text">
                  {{ formatCreatedAt(item.created_at) }}
                </div>
              </template>

              <span>{{ formatCreatedAt(item.created_at, true) }}</span>
            </v-tooltip>
          </template>

          <template v-slot:item.acoes="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  color="#222222"
                  :to="`/pedidos/${item.id}`"
                  small
                  icon
                >
                  <v-icon size="16"> mdi-pencil-outline </v-icon>
                </v-btn>
              </template>

              <span>Editar</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="$can('remove_fazenda')"
                  v-bind="attrs"
                  v-on="on"
                  @click="toggleDelete(item)"
                  class="ml-1"
                  color="error"
                  small
                  icon
                >
                  <v-icon size="20"> mdi-delete-outline </v-icon>
                </v-btn>
              </template>

              <span>Excluir</span>
            </v-tooltip>
          </template>
        </DataTableTemplate>
      </template>
    </PageListTemplate>

    <v-dialog
      v-if="item?.id && dialogDelete"
      v-model="dialogDelete"
      max-width="500"
      persistent
    >
      <DeleteItemDialog
        @confirm="onDelete"
        @close="toggleDelete"
        title="Apagar pedido"
        :name="item.nome"
      />
    </v-dialog>
  </div>
</template>

<script>
import PageListTemplate from "@/components/templates/PageList.vue";
import DataTableTemplate from "@/components/templates/DataTable.vue";
import DeleteItemDialog from "@/components/dialogs/DeleteItem.vue";
import formatCreatedAt from "@/utils/formatCreatedAt";
import formatPurpose from "@/utils/formatPurpose";
import formatBusiness from "@/utils/formatBusiness";
import formatStatus from "@/utils/formatStatus";
import formatAreaMinMax from "@/utils/formatAreaMinMax";
import formatPriceMinMax from "@/utils/formatPriceMinMax";
import colorPurposeById from "@/utils/colorPurposeById";
import { mapActions } from "vuex";

export default {
  components: { PageListTemplate, DeleteItemDialog, DataTableTemplate },
  data: () => ({
    title: "Pedidos",
    subtitle: "Acompanhe os interesses dos seus clientes",
    page: 1,
    isLoading: true,
    items: [],
    headers: [
      {
        text: "Código",
        align: "start",
        value: "id",
        width: 100,
      },
      {
        text: "Cliente",
        align: "start",
        value: "client_id",
      },
      {
        text: "Área total",
        align: "start",
        value: "total_area_min",
      },
      {
        text: "Área aberta",
        align: "start",
        value: "open_area_min",
      },
      {
        text: "Negociação",
        align: "start",
        value: "business",
      },
      {
        text: "Finalidade",
        align: "start",
        value: "purpose",
      },
      {
        text: "Valor",
        align: "start",
        value: "price_min",
      },
      {
        text: "Status",
        align: "start",
        value: "status",
      },
      {
        text: "Registro",
        align: "start",
        value: "created_at",
      },
      {
        text: "Ações",
        align: "center",
        sortable: false,
        value: "acoes",
        width: 120,
      },
    ],
    item: null,
    dialogDelete: false,
    isLoadingDelete: false,
  }),
  computed: {
    tableHeight() {
      const name = this.$vuetify.breakpoint.name;

      const offset = name === "sm" ? 235 : name === "xs" ? 210 : 244;

      return `calc(100dvh - ${offset}px)`;
    },
    add() {
      if (!this.$can("add_fazenda")) return;

      return {
        link: "/pedidos/adicionar",
        text: "Novo Pedido",
      };
    },
  },
  methods: {
    ...mapActions("orders", ["listOrder", "destroyOrder"]),
    async onLoad(page = 1, itemsPerPage = 10000) {
      this.page = page;
      this.isLoading = true;

      let filters = {
        page: page,
        itemsPerPage,
        status: 0,
      };

      const res = await this.listOrder(filters);

      this.items = res.data;
      this.isLoading = false;
    },
    async onDelete() {
      this.isLoadingDelete = true;

      const r = await this.destroyOrder(this.item.id);

      if (r) {
        this.$eventHub.$emit("snackBar", {
          icon: "mdi-account-remove",
          message: "Pedido excluído com sucesso.",
          type: "success",
        });

        this.item = null;
        this.dialogDelete = false;
        this.onLoad();
      } else {
        this.$eventHub.$emit("snackBar", {
          icon: "mdi-account-alert",
          message: "Não foi possível excluir o pedido. Tente novamente.",
          type: "error",
        });
      }

      this.isLoadingDelete = false;
    },
    toggleDelete(item) {
      if (this.dialogDelete) {
        this.dialogDelete = false;
        this.item = null;
      } else {
        this.item = item;
        this.dialogDelete = true;
      }
    },
    colorByType(type) {
      const types = {
        owner: {
          backgroundColor: "#e7eefe",
          color: "#155dfc",
        },
        captivator: {
          backgroundColor: "#d0fae5",
          color: "#007a55",
        },
        contact: {
          backgroundColor: "#fef3c6",
          color: "#bb4d00",
        },
      };

      return types[type] || null;
    },
    formatCreatedAt,
    formatPurpose,
    formatBusiness,
    formatStatus,
    formatAreaMinMax,
    formatPriceMinMax,
    colorPurposeById,
  },
  async mounted() {
    this.onLoad();
  },
};
</script>
